import { useState, useEffect } from 'react';
import { PageHeader, Badge, cx, type ToneType } from './shared';
import { Icon } from './ui/icons';

interface SecurityAlert {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'triggered' | 'acknowledged' | 'investigating' | 'resolved' | 'suppressed';
  triggered_at: string;
  acknowledged_at?: string;
  resolved_at?: string;
  rule_id: string;
  rule_name: string;
  source: string;
  assignee?: string;
  event_count: number;
  last_event_at: string;
  tags: string[];
  affected_assets: string[];
}

const mockAlerts: SecurityAlert[] = [
  {
    id: 'alert-001',
    title: 'Multiple Failed Login Attempts',
    description: 'User account "admin@company.com" has 15 failed login attempts in the last 5 minutes from IP 185.220.101.182',
    severity: 'critical',
    status: 'triggered',
    triggered_at: '2026-05-22T10:45:00Z',
    rule_id: 'rule-001',
    rule_name: 'Brute Force Login Detection',
    source: 'Azure AD',
    event_count: 15,
    last_event_at: '2026-05-22T10:44:30Z',
    tags: ['brute-force', 'authentication', 'suspicious-ip'],
    affected_assets: ['admin@company.com', '185.220.101.182']
  },
  {
    id: 'alert-002',
    title: 'Potential Data Exfiltration',
    description: 'Unusual large data transfer detected: 2.5GB uploaded to external service from production database',
    severity: 'high',
    status: 'acknowledged',
    triggered_at: '2026-05-22T09:30:00Z',
    acknowledged_at: '2026-05-22T09:35:00Z',
    rule_id: 'rule-002',
    rule_name: 'Large Data Transfer Detection',
    source: 'AWS CloudTrail',
    assignee: 'incident-team',
    event_count: 3,
    last_event_at: '2026-05-22T09:33:15Z',
    tags: ['data-exfiltration', 'aws', 'database'],
    affected_assets: ['prod-db-01', 'external-service.com']
  },
  {
    id: 'alert-003',
    title: 'Malware Detected on Endpoint',
    description: 'CrowdStrike detected and quarantined malware: Trojan.Win32.GenKryptik on LAPTOP-DEV-003',
    severity: 'high',
    status: 'investigating',
    triggered_at: '2026-05-22T08:15:00Z',
    acknowledged_at: '2026-05-22T08:20:00Z',
    rule_id: 'rule-003',
    rule_name: 'Malware Detection',
    source: 'CrowdStrike',
    assignee: 'security-ops',
    event_count: 1,
    last_event_at: '2026-05-22T08:15:00Z',
    tags: ['malware', 'endpoint', 'trojan'],
    affected_assets: ['LAPTOP-DEV-003', 'user.smith']
  },
  {
    id: 'alert-004',
    title: 'Privilege Escalation Attempt',
    description: 'User "contractor1" attempted to access admin resources without proper authorization',
    severity: 'medium',
    status: 'resolved',
    triggered_at: '2026-05-22T07:00:00Z',
    acknowledged_at: '2026-05-22T07:05:00Z',
    resolved_at: '2026-05-22T07:30:00Z',
    rule_id: 'rule-004',
    rule_name: 'Unauthorized Admin Access',
    source: 'Application Logs',
    assignee: 'security-ops',
    event_count: 5,
    last_event_at: '2026-05-22T07:10:00Z',
    tags: ['privilege-escalation', 'unauthorized-access'],
    affected_assets: ['contractor1', 'admin-panel']
  },
  {
    id: 'alert-005',
    title: 'Suspicious Network Activity',
    description: 'Outbound connections to known C2 server detected from internal host 192.168.1.45',
    severity: 'medium',
    status: 'suppressed',
    triggered_at: '2026-05-22T06:30:00Z',
    rule_id: 'rule-005',
    rule_name: 'C2 Communication Detection',
    source: 'Network IDS',
    event_count: 12,
    last_event_at: '2026-05-22T06:45:00Z',
    tags: ['c2', 'network', 'suspicious'],
    affected_assets: ['192.168.1.45', 'malicious-domain.com']
  }
];

const severityTone: Record<string, ToneType> = {
  critical: 'crit',
  high: 'warn',
  medium: 'info',
  low: 'muted'
};

const statusTone: Record<string, ToneType> = {
  triggered: 'crit',
  acknowledged: 'warn',
  investigating: 'info',
  resolved: 'ok',
  suppressed: 'muted'
};

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function SecurityAlerts() {
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('active');
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    const loadAlerts = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setAlerts(mockAlerts);
      setLoading(false);
    };
    
    loadAlerts();
  }, []);

  const filteredAlerts = alerts.filter(alert => {
    const isActive = ['triggered', 'acknowledged', 'investigating'].includes(alert.status);
    const matchesFilter = filter === 'all' || 
      (filter === 'active' && isActive) ||
      alert.severity === filter || 
      alert.status === filter;
    
    const matchesSearch = search === '' || 
      alert.title.toLowerCase().includes(search.toLowerCase()) ||
      alert.description.toLowerCase().includes(search.toLowerCase()) ||
      alert.rule_name.toLowerCase().includes(search.toLowerCase()) ||
      alert.source.toLowerCase().includes(search.toLowerCase()) ||
      alert.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const alertStats = {
    total: alerts.length,
    active: alerts.filter(a => ['triggered', 'acknowledged', 'investigating'].includes(a.status)).length,
    critical: alerts.filter(a => a.severity === 'critical').length,
    high: alerts.filter(a => a.severity === 'high').length
  };

  const handleAcknowledge = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId 
        ? { ...alert, status: 'acknowledged' as const, acknowledged_at: new Date().toISOString() }
        : alert
    ));
  };

  const handleResolve = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId 
        ? { ...alert, status: 'resolved' as const, resolved_at: new Date().toISOString() }
        : alert
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading security alerts...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Security Alerts"
        subtitle="Manage and respond to automated security alerts from your detection rules."
        right={
          <div className="flex items-center gap-3">
            <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40">
              <Icon name="download" size={14} stroke="#121111" /> Export
            </button>
            <button className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:opacity-90">
              <Icon name="settings" size={14} stroke="white" /> Alert Rules
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Total Alerts</div>
          <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{alertStats.total}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Active</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#CA9401' }}>{alertStats.active}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Critical</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#F40B0B' }}>{alertStats.critical}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">High</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#CA9401' }}>{alertStats.high}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Icon name="search" size={16} className="text-ink/40" />
          <input
            type="text"
            placeholder="Search alerts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="font-display text-[14px] text-ink placeholder-ink/40 border-none outline-none bg-transparent"
          />
        </div>
        
        <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white"
          >
            <option value="active">Active Alerts</option>
            <option value="all">All Alerts</option>
            <option value="triggered">Triggered</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
          </select>
        </div>
      </div>

      {/* Alerts List */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/60">
          Security Alerts ({filteredAlerts.length})
        </div>
        
        {filteredAlerts.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mb-4 text-ink/40">
              <Icon name="bell" size={48} />
            </div>
            <h3 className="font-display font-bold text-ink text-[18px] mb-2">No alerts found</h3>
            <p className="font-display text-[14px] text-ink/60">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : (
          <ul>
            {filteredAlerts.map((alert) => (
              <li 
                key={alert.id} 
                className="px-5 py-4 hover:bg-[#FCF8F1] border-b border-[#F7EDDB] last:border-0"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-display font-bold text-ink text-[16px]">{alert.title}</h3>
                      <Badge tone={severityTone[alert.severity]} dot className="capitalize">
                        {alert.severity}
                      </Badge>
                      <Badge tone={statusTone[alert.status]} className="capitalize">
                        {alert.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    
                    <p className="font-display text-[14px] text-ink/65 mb-3 line-clamp-2">
                      {alert.description}
                    </p>
                    
                    <div className="flex items-center gap-4 text-[12px] text-ink/50 mb-3">
                      <span><strong>Rule:</strong> {alert.rule_name}</span>
                      <span><strong>Source:</strong> {alert.source}</span>
                      <span><strong>Events:</strong> {alert.event_count}</span>
                      <span><strong>Triggered:</strong> {formatTimeAgo(alert.triggered_at)}</span>
                      {alert.assignee && <span><strong>Assignee:</strong> @{alert.assignee}</span>}
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex flex-wrap gap-1">
                        {alert.tags.map(tag => (
                          <span key={tag} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#EDE5FC] text-[#7F56D9] font-display text-[10px] font-semibold">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex flex-wrap gap-1 ml-2">
                        {alert.affected_assets.map(asset => (
                          <span key={asset} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#F7EDDB] text-[#CA9401] font-data text-[10px] font-semibold">
                            {asset}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {alert.status === 'triggered' && (
                      <button
                        onClick={() => handleAcknowledge(alert.id)}
                        className="h-8 px-3 rounded-[6px] bg-[#CA9401] text-white font-display font-bold text-[12px] hover:opacity-90"
                      >
                        Acknowledge
                      </button>
                    )}
                    {['triggered', 'acknowledged', 'investigating'].includes(alert.status) && (
                      <button
                        onClick={() => handleResolve(alert.id)}
                        className="h-8 px-3 rounded-[6px] bg-[#159D4C] text-white font-display font-bold text-[12px] hover:opacity-90"
                      >
                        Resolve
                      </button>
                    )}
                    <button className="h-8 w-8 rounded-[6px] border border-[#F3E4C8] bg-white grid place-items-center hover:border-ink/40">
                      <Icon name="more-horizontal" size={14} stroke="#121111" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}